import { useEffect, useId, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { AlertTriangle, ChevronDown, Clock, History, Settings2, HelpCircle } from 'lucide-react';
import { MIN_TICKET_AGE_MINUTES, type CompanyAutomationView } from '@autopilot/contracts';
import { api } from '../../api/client';
import { qk } from '../../api/hooks';
import { ApiError, isConflict } from '../../api/errors';
import { ScoreThreshold } from '../../components/ScoreThreshold';
import { EmptyState, ErrorAlert, QueryBoundary } from '../../components/States';
import { ToneBadge } from '../../components/StatusBadge';
import { EFFECTIVE_STATE_LABEL, SCHEDULE_RUN_STATUS_LABEL } from '../../lib/labels';
import { formatDateTime, formatMinutes, relativeAge, shortId } from '../../lib/format';
import { previewNextRun, validateInterval } from '../../lib/interval';
import { useIdempotencyKey } from '../../lib/idempotency';
import { modelUsable } from '../../lib/models';

export const CSAT_PLACEHOLDER = 'Noch nicht verfügbar';

/** Backlog nie als 0 bei Fehler: veraltet mit Stand oder „Nicht verfügbar“ (08 §6). */
export function backlogDisplay(b: CompanyAutomationView['backlog'], now = Date.now()): { value: string; hint: string; stale: boolean; unavailable: boolean } {
  if (b.state === 'UNAVAILABLE' || b.count === null) {
    return { value: 'Nicht verfügbar', hint: b.asOf ? `letzter Stand ${formatDateTime(b.asOf)}` : 'Kein bekannter Wert', stale: false, unavailable: true };
  }
  const value = b.count.toLocaleString('de-DE');
  if (b.state === 'STALE') return { value, hint: `veraltet · Stand ${formatDateTime(b.asOf)}`, stale: true, unavailable: false };
  return { value, hint: b.asOf ? `Stand ${relativeAge(b.asOf, now)}` : '', stale: false, unavailable: false };
}

function AutomationState({ c }: { c: CompanyAutomationView }) {
  const eff = EFFECTIVE_STATE_LABEL[c.effectiveState];
  return (
    <Box>
      <ToneBadge tone={eff.tone} label={eff.label} spinning={c.effectiveState === 'SYNCING' || c.effectiveState === 'DISABLING'} data-testid={`state-${c.gesellschaftId}`} />
      <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.5 }}>
        Gewünscht: {c.desiredEnabled ? 'An' : 'Aus'}
        {c.syncState === 'FAILED' && ' · Übertragung an Nidus fehlgeschlagen'}
        {c.syncState === 'PENDING' && ' · Bestätigung durch Nidus ausstehend'}
      </Typography>
      {c.blockedReason && (
        <Typography variant="caption" component="div" sx={{ color: 'error.dark', display: 'flex', gap: 0.5, alignItems: 'flex-start', mt: 0.25 }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden /> {c.blockedReason}
        </Typography>
      )}
    </Box>
  );
}

export function CompaniesTab() {
  const [edit, setEdit] = useState<CompanyAutomationView | null>(null);
  const [runsFor, setRunsFor] = useState<CompanyAutomationView | null>(null);
  const q = useQuery({
    queryKey: qk.companies,
    queryFn: () => api.listCompanies({ limit: 200 }),
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      return items.some((c) => c.effectiveState === 'SYNCING' || c.effectiveState === 'DISABLING' || c.syncState === 'PENDING') ? 2000 : 60000;
    },
  });
  const items = q.data?.items ?? [];
  return (
    <>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, maxWidth: 900 }}>
        Automatische Läufe wählen je Gesellschaft im eingestellten Intervall alle mindestens {MIN_TICKET_AGE_MINUTES} Minuten alten, aktiven Tickets aus, erstellen Vorschläge, lassen beide Prüfer bewerten und versenden passende Lösungen ohne weiteren Klick. Nicht geeignete Tickets bleiben offen.
      </Typography>
      <CompanyHelp />
      <Card sx={{ mt: 2 }}>
        <QueryBoundary query={q} action="Gesellschaften laden" loadingRows={5}>
          {items.length === 0 ? (
            <EmptyState title="Keine Gesellschaften" description="Für Ihren Gesellschaftsscope sind keine Gesellschaften freigegeben." />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table aria-label="Gesellschaften und Automatik" sx={{ minWidth: 1000, '& td, & th': { px: 1.25 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Gesellschaft</TableCell>
                    <TableCell>Modell / Prüfprofil</TableCell>
                    <TableCell>Score-Grenzen</TableCell>
                    <TableCell>Intervall</TableCell>
                    <TableCell>Automatik</TableCell>
                    <TableCell>Läufe</TableCell>
                    <TableCell align="right">Backlog</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal !important', lineHeight: 1.2 }}>Kunden&shy;zufriedenheit</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((c) => {
                    const bl = backlogDisplay(c.backlog);
                    return (
                      <TableRow key={c.gesellschaftId} data-testid={`company-row-${c.gesellschaftName}`}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {c.gesellschaftName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Regel v{c.policyVersion}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{c.generator?.name ?? <em style={{ color: '#535C70' }}>kein Modell</em>}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {c.reviewProfile ? `${c.reviewProfile.name} v${c.reviewProfile.version}` : 'kein Prüfprofil'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">Richtigkeit ≥ {c.minCorrectness}</Typography>
                          <Typography variant="body2">Fehlerrisiko ≤ {c.maxErrorRisk}</Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatMinutes(c.intervalMinutes)}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <AutomationState c={c} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 230 }}>
                          <Typography variant="body2">Nächster: {c.nextRunAt ? formatDateTime(c.nextRunAt) : '–'}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Letzter:{' '}
                            {c.lastRun ? (
                              <>
                                {formatDateTime(c.lastRun.scheduledFor)} · {SCHEDULE_RUN_STATUS_LABEL[c.lastRun.status as keyof typeof SCHEDULE_RUN_STATUS_LABEL]?.label ?? c.lastRun.status} · {c.lastRun.resolved} gelöst
                              </>
                            ) : (
                              '–'
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" data-testid={`backlog-${c.gesellschaftName}`}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: bl.unavailable ? 'text.secondary' : 'text.primary', display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                            {bl.stale && <AlertTriangle size={14} color="#7A5410" aria-hidden />}
                            {bl.value}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ color: bl.stale ? 'var(--color-warning)' : 'text.secondary' }}>
                            {bl.hint}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="csat">
                            {CSAT_PLACEHOLDER}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                            <Button size="small" variant="outlined" onClick={() => setEdit(c)} startIcon={<Settings2 size={15} />} data-testid={`edit-company-${c.gesellschaftName}`} sx={{ whiteSpace: 'nowrap' }}>
                              Einstellungen
                            </Button>
                            <Tooltip title="Laufhistorie inkl. leerer, verpasster und übersprungener Slots">
                              <Button size="small" onClick={() => setRunsFor(c)} startIcon={<History size={15} />} sx={{ whiteSpace: 'nowrap' }}>
                                Laufhistorie
                              </Button>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </QueryBoundary>
      </Card>
      {edit && <CompanyDialog company={edit} onClose={() => setEdit(null)} />}
      {runsFor && <RunsDialog company={runsFor} onClose={() => setRunsFor(null)} />}
    </>
  );
}

function CompanyHelp() {
  return (
    <Accordion disableGutters sx={{ borderRadius: '12px !important', border: '1px solid #E1E5EC', boxShadow: 'none', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ChevronDown size={18} />}>
        <Typography variant="subtitle2" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <HelpCircle size={16} aria-hidden /> Hilfe: Was bedeutet „Automatische Läufe“?
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" component="div" sx={{ '& li': { mb: 0.5 } }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Die Regel gehört der Installation, nicht Ihrer Sitzung. Abmelden oder ein abgelaufenes Admin-Token stoppt sie nicht; auch der Entzug eines einzelnen Admin-Logins deaktiviert eine erteilte Regel nicht.</li>
            <li>Speichern mit „Automatische Läufe“ an autorisiert künftige Läufe unter genau dieser Regelversion – ohne weitere Freigabe je Batch.</li>
            <li>„Aus“ sperrt sofort neue automatische Auswahl, Modellaufrufe und Einreichungen. Bereits von Nidus angenommene Aufträge laufen noch zu Ende; Abschalten ist keine Rücknahme.</li>
            <li>„Aus“ betrifft nur automatische Läufe – manuelle Batches bleiben möglich. Der globale Not-Stopp (Administration → Betrieb) sperrt dagegen neue Schreiboperationen beider Modi.</li>
            <li>Stoppen ist auch über den Entzug der Service- oder Gesellschaftsrechte möglich. Mindestalter der Tickets: fest {MIN_TICKET_AGE_MINUTES} Minuten.</li>
          </ul>
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

const FIELD_LABELS: Record<string, string> = {
  generatorModelConfigId: 'KI-Modell',
  reviewProfileId: 'Prüfprofil',
  minCorrectness: 'Richtigkeit mindestens',
  maxErrorRisk: 'Fehlerrisiko höchstens',
  intervalMinutes: 'Intervall',
  desiredEnabled: 'Automatische Läufe',
};

export function CompanyDialog({ company, onClose }: { company: CompanyAutomationView; onClose: () => void }) {
  const titleId = useId();
  const qc = useQueryClient();
  const idem = useIdempotencyKey();
  const models = useQuery({ queryKey: qk.models, queryFn: api.listModels });
  const profiles = useQuery({ queryKey: qk.reviewProfiles, queryFn: api.listReviewProfiles });
  const [base, setBase] = useState(company);
  const [enabled, setEnabled] = useState(company.desiredEnabled);
  const [gen, setGen] = useState(company.generator?.modelConfigId ?? '');
  const [rp, setRp] = useState(company.reviewProfile?.id ?? '');
  const [min, setMin] = useState(company.minCorrectness);
  const [max, setMax] = useState(company.maxErrorRisk);
  const [intervalText, setIntervalText] = useState(String(company.intervalMinutes));
  const [saved, setSaved] = useState<CompanyAutomationView | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState(false);

  const interval = Number(intervalText);
  const intervalError = validateInterval(intervalText.trim() === '' ? null : interval);
  const genModels = (models.data ?? []).filter((m) => m.roles.includes('GENERATOR'));
  const activeProfiles = (profiles.data ?? []).filter((p) => p.active || p.id === rp);

  const dirty =
    enabled !== base.desiredEnabled ||
    gen !== (base.generator?.modelConfigId ?? '') ||
    rp !== (base.reviewProfile?.id ?? '') ||
    min !== base.minCorrectness ||
    max !== base.maxErrorRisk ||
    interval !== base.intervalMinutes;

  const preview = useMemo(
    () =>
      previewNextRun({
        wasEnabled: base.desiredEnabled,
        willEnable: enabled,
        oldInterval: base.intervalMinutes,
        newInterval: intervalError ? base.intervalMinutes : interval,
        currentNextRunAt: base.nextRunAt,
      }),
    [base, enabled, interval, intervalError],
  );

  // Laufenden Sync nach dem Speichern verfolgen
  const live = useQuery({
    queryKey: [...qk.companies, company.gesellschaftId, 'automation'],
    queryFn: () => api.getCompanyAutomation(company.gesellschaftId),
    enabled: !!saved,
    initialData: saved ?? undefined,
    refetchInterval: (query) => {
      const d = query.state.data;
      return d && (d.effectiveState === 'SYNCING' || d.effectiveState === 'DISABLING' || d.syncState === 'PENDING') ? 2000 : false;
    },
  });
  const after = live.data ?? saved;
  useEffect(() => {
    if (after) void qc.invalidateQueries({ queryKey: qk.companies, exact: true });
  }, [after?.effectiveState, after?.syncState]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => {
      const body = {
        expectedVersion: base.policyVersion,
        desiredEnabled: enabled,
        generatorModelConfigId: gen || null,
        reviewProfileId: rp || null,
        minCorrectness: min,
        maxErrorRisk: max,
        intervalMinutes: interval,
      };
      return api.putCompanyAutomation(company.gesellschaftId, body, idem.keyFor({ id: company.gesellschaftId, ...body }));
    },
    onSuccess: (res) => {
      idem.reset();
      setFieldErrors({});
      setConflict(false);
      setSaved(res);
      setBase(res);
      qc.setQueryData([...qk.companies, company.gesellschaftId, 'automation'], res);
      void qc.invalidateQueries({ queryKey: qk.companies, exact: true });
    },
    onError: async (err) => {
      if (err instanceof ApiError && err.status === 422) setFieldErrors(err.fieldErrors);
      if (isConflict(err)) {
        idem.reset();
        setConflict(true);
        try {
          const fresh = await api.getCompanyAutomation(company.gesellschaftId);
          setBase(fresh);
        } catch {
          /* Anzeige des Konflikts genügt */
        }
        void qc.invalidateQueries({ queryKey: qk.companies });
      }
    },
  });

  const genericErrors = Object.entries(fieldErrors).filter(([k]) => k === 'desiredEnabled' || !(k in FIELD_LABELS));

  return (
    <Dialog open onClose={() => !save.isPending && onClose()} aria-labelledby={titleId} maxWidth="md" fullWidth data-testid="company-dialog">
      <DialogTitle id={titleId}>
        Automatik: {company.gesellschaftName}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Aktuelle Regelversion v{base.policyVersion} · {base.configuredBy ? `zuletzt geändert von ${base.configuredBy}, ${formatDateTime(base.configuredAt)}` : 'noch nicht konfiguriert'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 3 }}>
          <Box sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: enabled ? 'primary.light' : 'divider', backgroundColor: enabled ? 'var(--color-primary-soft)' : '#FAFBFC' }}>
            <FormControlLabel
              control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} slotProps={{ input: { 'aria-describedby': `${titleId}-switch-help` } }} data-testid="automation-switch" />}
              label={<Typography variant="subtitle1">Automatische Läufe {enabled ? 'an' : 'aus'}</Typography>}
            />
            <Typography id={`${titleId}-switch-help`} variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Wenn an, werden nach dem Speichern und der Bestätigung durch Nidus in jedem Intervall passende Tickets ohne weiteren Start-Klick beantwortet und gelöst. Der Schalter blockiert keine manuellen Batches; der globale Not-Stopp wird separat unter „Betrieb“ gesteuert.
            </Typography>
            {fieldErrors.desiredEnabled && (
              <Typography variant="body2" sx={{ color: 'error.dark', mt: 1, display: 'flex', gap: 0.5 }} role="alert">
                <AlertTriangle size={16} aria-hidden /> {fieldErrors.desiredEnabled}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <FormControl fullWidth size="small" error={!!fieldErrors.generatorModelConfigId}>
              <InputLabel id={`${titleId}-gen`}>KI-Modell (Generator)</InputLabel>
              <Select labelId={`${titleId}-gen`} label="KI-Modell (Generator)" value={gen} onChange={(e) => setGen(e.target.value)} data-testid="company-model">
                <MenuItem value="">
                  <em>Kein Modell</em>
                </MenuItem>
                {genModels.map((m) => (
                  <MenuItem key={m.id} value={m.id} disabled={!modelUsable(m)}>
                    {m.name} ({m.modelId}){!m.active ? ' – deaktiviert' : !modelUsable(m) ? ' – kein Schlüssel' : ''}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{fieldErrors.generatorModelConfigId ?? 'Kein automatischer Ersatz bei deaktiviertem Modell.'}</FormHelperText>
            </FormControl>
            <FormControl fullWidth size="small" error={!!fieldErrors.reviewProfileId}>
              <InputLabel id={`${titleId}-rp`}>Prüfprofil</InputLabel>
              <Select labelId={`${titleId}-rp`} label="Prüfprofil" value={rp} onChange={(e) => setRp(e.target.value)} data-testid="company-profile">
                <MenuItem value="">
                  <em>Kein Prüfprofil</em>
                </MenuItem>
                {activeProfiles.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} v{p.version}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{fieldErrors.reviewProfileId ?? 'Zwei unabhängige Prüfer; Version wird mit der Regel festgehalten.'}</FormHelperText>
            </FormControl>
            <ScoreThreshold label="Richtigkeit mindestens" value={min} onChange={setMin} direction="min" error={fieldErrors.minCorrectness} helper="Inklusiv (≥). Vorschlag: 90." testId="company-min" />
            <ScoreThreshold label="Fehlerrisiko höchstens" value={max} onChange={setMax} direction="max" error={fieldErrors.maxErrorRisk} helper="Inklusiv (≤). Vorschlag: 10." testId="company-max" />
            <TextField
              label="Intervall in Minuten"
              type="number"
              value={intervalText}
              onChange={(e) => setIntervalText(e.target.value)}
              error={!!intervalError || !!fieldErrors.intervalMinutes}
              helperText={intervalError ?? fieldErrors.intervalMinutes ?? `15–1.440 in Schritten von 15 · entspricht ${formatMinutes(interval)}`}
              slotProps={{ htmlInput: { min: 15, max: 1440, step: 15 } }}
              data-testid="company-interval"
            />
            <TextField
              label="Ticket-Mindestalter"
              value={`${MIN_TICKET_AGE_MINUTES} Minuten`}
              disabled
              helperText="Fest vorgegeben, im MVP nicht änderbar."
              slotProps={{ input: { readOnly: true } }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: 'text.secondary' }} data-testid="next-run-preview">
            <Clock size={16} aria-hidden />
            <Typography variant="body2">
              {after
                ? `Nächster Lauf laut Server: ${after.nextRunAt ? formatDateTime(after.nextRunAt) : '– (keine automatischen Läufe)'}`
                : enabled
                  ? `Nächster Lauf nach dem Speichern: ${preview.at ? formatDateTime(preview.at) : '–'}${preview.newAnchor ? ' (neuer Zeitanker: ein Intervall nach der Bestätigung)' : ' (Zeitplan bleibt unverändert)'}`
                  : 'Nach dem Speichern finden keine automatischen Läufe statt.'}
            </Typography>
          </Box>
          {after && (
            <Alert
              severity={after.effectiveState === 'BLOCKED' ? 'error' : after.effectiveState === 'ENABLED' || after.effectiveState === 'DISABLED' ? 'success' : 'info'}
              icon={after.effectiveState === 'SYNCING' || after.effectiveState === 'DISABLING' ? <CircularProgress size={18} /> : undefined}
              data-testid="company-saved"
            >
              Gespeichert als Regelversion v{after.policyVersion}. Status: <strong>{EFFECTIVE_STATE_LABEL[after.effectiveState].label}</strong>
              {after.effectiveState === 'SYNCING' && ' – wird erst nach Bestätigung durch Nidus aktiv.'}
              {after.effectiveState === 'DISABLING' && ' – bis zur Bestätigung durch Nidus können bereits übergebene Anfragen noch angenommen werden.'}
              {after.blockedReason && ` – ${after.blockedReason}`}
            </Alert>
          )}
          {conflict && (
            <Alert severity="warning" icon={<AlertTriangle size={20} />}>
              Die Regel wurde inzwischen von jemand anderem geändert (jetzt v{base.policyVersion}). Ihre Eingaben sind noch sichtbar; prüfen Sie sie und speichern Sie erneut.
            </Alert>
          )}
          {save.error && !isConflict(save.error) && (
            <ErrorAlert error={save.error} action="Gesellschaftsregel speichern" />
          )}
          {genericErrors.length > 0 && (
            <Alert severity="error" icon={<AlertTriangle size={20} />}>
              {genericErrors.map(([k, v]) => (
                <div key={k}>{v}</div>
              ))}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 'auto' }}>
          Reglerbewegungen starten nichts. Erst „Speichern“ übernimmt die Regel.
        </Typography>
        <Button onClick={onClose} disabled={save.isPending}>
          {saved && !dirty ? 'Schließen' : 'Abbrechen'}
        </Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={!dirty || !!intervalError || save.isPending} data-testid="company-save">
          {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RunsDialog({ company, onClose }: { company: CompanyAutomationView; onClose: () => void }) {
  const titleId = useId();
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const q = useQuery({
    queryKey: [...qk.companyRuns(company.gesellschaftId), cursors.at(-1)],
    queryFn: () => api.companyRuns(company.gesellschaftId, { cursor: cursors.at(-1), limit: 20 }),
  });
  const items = q.data?.items ?? [];
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="md" fullWidth>
      <DialogTitle id={titleId}>Laufhistorie: {company.gesellschaftName}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Enthält auch leere, verpasste und wegen eines noch laufenden Batches übersprungene Slots. Nach einem Ausfall wird höchstens ein Nachhollauf gestartet.
        </Typography>
        <QueryBoundary query={q} action="Laufhistorie laden" loadingRows={5}>
          {items.length === 0 ? (
            <EmptyState title="Noch keine Läufe" description="Nach der Aktivierung erscheint hier jeder Zeitplan-Slot." />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Slot</TableCell>
                    <TableCell>Ergebnis</TableCell>
                    <TableCell>Regel</TableCell>
                    <TableCell>Laufzeit</TableCell>
                    <TableCell>Batch</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((r) => {
                    const s = SCHEDULE_RUN_STATUS_LABEL[r.status];
                    return (
                      <TableRow key={r.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.scheduledFor)}</TableCell>
                        <TableCell>
                          <ToneBadge tone={s.tone} label={s.label} />
                          {r.missedSlotCount > 0 && (
                            <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                              {r.missedSlotCount} verpasste Slots zusammengefasst
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          v{r.policyVersion} · Gen. {r.scheduleGeneration}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {r.startedAt ? `${formatDateTime(r.startedAt)}${r.finishedAt ? ` – ${formatDateTime(r.finishedAt).split(', ')[1] ?? ''}` : ' – läuft'}` : '–'}
                        </TableCell>
                        <TableCell>
                          {r.batchId ? (
                            <Link component={RouterLink} to={`/batches/${r.batchId}`} onClick={onClose}>
                              Batch {shortId(r.batchId)}
                            </Link>
                          ) : (
                            '–'
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </QueryBoundary>
      </DialogContent>
      <DialogActions>
        <Button disabled={cursors.length <= 1} onClick={() => setCursors((c) => c.slice(0, -1))}>
          Neuere
        </Button>
        <Button disabled={!q.data?.nextCursor} onClick={() => setCursors((c) => [...c, q.data!.nextCursor!])}>
          Ältere
        </Button>
        <Button variant="contained" onClick={onClose}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
